import { format, isValid, subDays } from 'date-fns';
import {
  OvulatoryEventComparisonRow,
  OvulatoryEventDayRangeQuery,
  OvulatoryEventPrediction,
  OvulatoryEventScorecardCounts,
} from '../../model/dto/ovulatory-event-prediction/ovulatory-event-prediction';
import { DailyData } from '../../model/dto/daily-data';
import { DateRange } from '../../model/type/date-range.type';
import {
  OvulatoryEventDayRow,
  OvulatoryEventScorecard,
  OvulatoryEventTableDay,
  OvulatoryEventWrongKind,
} from '../../model/type/ovulatory-event-prediction.type';
import DateUtils from './date-utils';

export const OVULATORY_EVENT_DEFAULT_RANGE_DAYS = 90 as const;

export const getOvulatoryEventRowKey = (
  day: string,
  modelVersion: string
): string => `${day}|${modelVersion}`;

export const joinOvulatoryEventDayRows = (
  comparison: OvulatoryEventComparisonRow[],
  predictions: OvulatoryEventPrediction[] = []
): OvulatoryEventDayRow[] => {
  const predictionsByKey = new Map(
    predictions.map((prediction) => [
      getOvulatoryEventRowKey(prediction.day, prediction.model_version),
      prediction,
    ])
  );

  return comparison.map((row) => {
    const key = getOvulatoryEventRowKey(row.day, row.model.model_version);

    return { key, comparison: row, prediction: predictionsByKey.get(key) };
  });
};

export const getOvulatoryEventWrongKind = ({
  model,
  observed_outcome,
}: OvulatoryEventComparisonRow): OvulatoryEventWrongKind | null => {
  if (
    model.state === 'approaching' &&
    observed_outcome === 'sufficient_negative'
  ) {
    return 'false_alarm';
  }

  if (model.state === 'not_approaching' && observed_outcome === 'positive') {
    return 'miss';
  }

  return null;
};

const ratio = (numerator: number, denominator: number): number | null =>
  denominator === 0 ? null : numerator / denominator;

export const toOvulatoryEventScorecard = ({
  approaching_positive,
  approaching_negative,
  not_approaching_positive,
  not_approaching_negative,
  excluded,
}: OvulatoryEventScorecardCounts): OvulatoryEventScorecard => ({
  approachingPositive: approaching_positive,
  approachingNegative: approaching_negative,
  notApproachingPositive: not_approaching_positive,
  notApproachingNegative: not_approaching_negative,
  counted:
    approaching_positive +
    approaching_negative +
    not_approaching_positive +
    not_approaching_negative,
  precision: ratio(
    approaching_positive,
    approaching_positive + approaching_negative
  ),
  recall: ratio(
    approaching_positive,
    approaching_positive + not_approaching_positive
  ),
  excluded,
});

export const OVULATORY_EVENT_PAGE_SIZE = 30 as const;

export const toOvulatoryEventPageOffset = (
  offset: number | null | undefined
): number =>
  typeof offset === 'number' && Number.isSafeInteger(offset) && offset > 0
    ? Math.floor(offset / OVULATORY_EVENT_PAGE_SIZE) * OVULATORY_EVENT_PAGE_SIZE
    : 0;

export const getOvulatoryEventLastPageOffset = (total: number): number =>
  total > 0
    ? Math.floor((total - 1) / OVULATORY_EVENT_PAGE_SIZE) *
      OVULATORY_EVENT_PAGE_SIZE
    : 0;

export const getOvulatoryEventPageDayRange = (
  rows: OvulatoryEventComparisonRow[]
): OvulatoryEventDayRangeQuery | null => {
  const days = rows.map(({ day }) => day).sort();

  return days.length === 0
    ? null
    : { day_from: days[0], day_to: days[days.length - 1] };
};

export const formatOvulatoryEventScore = (score: number): string =>
  score.toFixed(3);

export const formatOvulatoryEventValue = (value: number): string =>
  String(Number(value.toFixed(4)));

export const formatOvulatoryEventRate = (rate: number): string =>
  `${(rate * 100).toFixed(1)}%`;

export const formatOvulatoryEventDay = (
  day: string,
  displayFormat = 'MMM d, yyyy'
): string => {
  const date = DateUtils.parseDayString(day);

  return date ? format(date, displayFormat) : day;
};

export const formatOvulatoryEventDayRange = (
  dayFrom: string,
  dayTo: string
): string => {
  const sameYear = dayFrom.slice(0, 4) === dayTo.slice(0, 4);

  return `${formatOvulatoryEventDay(
    dayFrom,
    sameYear ? 'MMM d' : 'MMM d, yyyy'
  )} - ${formatOvulatoryEventDay(dayTo)}`;
};

export const formatOvulatoryEventScoredAt = (updatedAt: string): string => {
  const date = new Date(updatedAt);

  return isValid(date)
    ? `${format(date, 'MMM d, yyyy, HH:mm')} UTC${format(date, 'xxx')}`
    : updatedAt;
};

export const getDefaultOvulatoryEventDateRange = (
  today: Date = new Date(),
  days: number = OVULATORY_EVENT_DEFAULT_RANGE_DAYS
): DateRange => ({ startDate: subDays(today, days - 1), endDate: today });

export const toOvulatoryEventDayRangeQuery = ({
  startDate,
  endDate,
}: DateRange): OvulatoryEventDayRangeQuery => ({
  day_from: DateUtils.getDbDateTag(startDate),
  day_to: DateUtils.getDbDateTag(endDate),
});

export const buildOvulatoryEventTableDays = (
  rows: OvulatoryEventDayRow[],
  dailyData: DailyData[] = []
): OvulatoryEventTableDay[] => {
  const scansByDay = new Map(
    dailyData.flatMap((scans): [string, DailyData][] =>
      scans.day ? [[scans.day, scans]] : []
    )
  );

  return rows.map((row) => ({
    row,
    scans: scansByDay.get(row.comparison.day),
  }));
};
